import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function validateNew(p) {
  if (p.length < 8) return "Use at least 8 characters.";
  if (!/[A-Z]/.test(p)) return "Include an uppercase letter.";
  if (!/[a-z]/.test(p)) return "Include a lowercase letter.";
  if (!/[0-9]/.test(p)) return "Include a number.";
  return null;
}

export default function AccountPage() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  // Feb 2026 - self-service email notification preference.
  // Default to true when the field is missing (existing users
  // keep receiving product notifications until they opt out).
  const emailNotificationsOn = user?.email_notifications_enabled !== false;
  const [emailPrefSaving, setEmailPrefSaving] = useState(false);

  const toggleEmailNotifications = async (next) => {
    setEmailPrefSaving(true);
    try {
      const { data } = await api.patch("/auth/email-notifications", {
        email_notifications_enabled: next,
      });
      setUser(data);
      toast.success(next
        ? "Email notifications on."
        : "Email notifications off. Important account, security or billing emails may still be sent.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not update preference.");
    } finally {
      setEmailPrefSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current_password) return toast.error("Enter your current password.");
    const err = validateNew(form.new_password);
    if (err) return toast.error(err);
    if (form.new_password !== form.confirm) return toast.error("New passwords don't match.");

    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated. Please sign in again.");
      // Sessions on this device stay valid via cookie; we still log out so the
      // user re-enters the new password and to be consistent with the "log
      // out other sessions after password reset" behaviour.
      await logout();
      navigate("/login");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="eyebrow">Account</div>
      <h1 className="font-display text-4xl mt-1 mb-2">My account</h1>
      <p className="text-sm text-[#4B5773] mb-8">Update your sign-in details. We&apos;ll email you a confirmation whenever your password changes.</p>

      <div className="max-w-xl space-y-6">
        <Card title="Profile" data-testid="account-profile-card">
          <dl className="text-sm grid grid-cols-3 gap-y-2.5">
            <dt className="text-[#4B5773]">Name</dt>
            <dd className="col-span-2 font-medium text-[#0B1A36]">{user.name || "-"}</dd>
            <dt className="text-[#4B5773]">Email</dt>
            <dd className="col-span-2 font-medium text-[#0B1A36]">{user.email}</dd>
            <dt className="text-[#4B5773]">Role</dt>
            <dd className="col-span-2 font-medium text-[#0B1A36] capitalize">{user.role.replace("_"," ")}</dd>
          </dl>
        </Card>

        <Card title="Email notifications" data-testid="account-email-prefs-card">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm text-[#0B1A36]">
                Receive non-critical product email notifications from NextRota.
              </p>
              <p className="text-xs text-[#4B5773] mt-1">
                Important account, security or billing emails may still be sent.
              </p>
            </div>
            <Switch
              data-testid="account-email-prefs-toggle"
              checked={emailNotificationsOn}
              disabled={emailPrefSaving}
              onCheckedChange={toggleEmailNotifications}
            />
          </div>
        </Card>

        <Card title="Change password" data-testid="change-password-card">
          <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
            <div>
              <Label className="eyebrow">Current password</Label>
              <Input required type="password" value={form.current_password}
                data-testid="cp-current"
                onChange={(e)=>setForm({...form, current_password:e.target.value})}
                className="mt-2 h-11 rounded-lg"/>
            </div>
            <div>
              <Label className="eyebrow">New password</Label>
              <Input required type="password" value={form.new_password}
                data-testid="cp-new"
                onChange={(e)=>setForm({...form, new_password:e.target.value})}
                className="mt-2 h-11 rounded-lg"/>
            </div>
            <div>
              <Label className="eyebrow">Confirm new password</Label>
              <Input required type="password" value={form.confirm}
                data-testid="cp-confirm"
                onChange={(e)=>setForm({...form, confirm:e.target.value})}
                className="mt-2 h-11 rounded-lg"/>
            </div>
            <p className="text-xs text-[#4B5773]">8+ characters with an uppercase, lowercase and a number.</p>
            <Button type="submit" disabled={busy} data-testid="cp-submit"
              className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
